import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { STORAGE_NAME } from "../global";

const SAVED_KEY = `${STORAGE_NAME}:saved`;
const CHANGE_EVENT = "paris-events:saved-change";

const SavedContext = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStorage(value) {
  try {
    if (Object.keys(value).length === 0) {
      localStorage.removeItem(SAVED_KEY);
    } else {
      localStorage.setItem(SAVED_KEY, JSON.stringify(value));
    }
  } catch {
    // Quota exceeded or storage disabled — ignore silently.
  }
}

export function SavedProvider({ children }) {
  const [saved, setSaved] = useState(readStorage);

  // Cross-tab sync via the native `storage` event, and same-tab sync via a custom event.
  useEffect(() => {
    const sync = () => setSaved(readStorage());
    const onStorage = (e) => {
      if (e.key === SAVED_KEY || e.key === null) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, []);

  const toggle = useCallback((id) => {
    setSaved((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = id;
      writeStorage(next);
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    writeStorage({});
    setSaved({});
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const isSaved = useCallback((id) => Boolean(saved[id]), [saved]);

  return (
    <SavedContext.Provider value={{ saved, toggle, clear, isSaved }}>
      {children}
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) throw new Error("useSaved must be used inside <SavedProvider>");
  return ctx;
}
